
import { useUser } from "../contexts/UserContext";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import AppointmentList from "@/components/dashboard/AppointmentList";
import ProfileCard from "@/components/dashboard/ProfileCard";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Users } from "lucide-react";
import { Link } from "react-router-dom";

// Interface para compatibilidade com AppointmentList
interface DashboardAppointment {
  id: string;
  psychologist: {
    name: string;
    photoUrl: string;
  };
  date: string;
  time: string;
  duration: number;
  status: string;
  meetingUrl?: string;
}

const Dashboard = () => {
  const { user, appointments } = useUser();

  // Mapeando os appointments do UserContext para o formato esperado pelo AppointmentList
  const mappedAppointments: DashboardAppointment[] = appointments.map(app => ({
    id: app.id,
    psychologist: {
      name: app.psychologist?.name || 'Psicólogo',
      photoUrl: app.psychologist?.photoUrl || '',
    },
    date: app.date,
    time: app.time,
    duration: app.duration,
    status: app.status,
    meetingUrl: app.meetingUrl,
  }));

  const upcomingAppointments = mappedAppointments.filter(app => 
    app.status === "scheduled" || 
    app.status === "pending_payment"
  );
  
  const pastAppointments = mappedAppointments.filter(app => 
    app.status === "completed" || 
    app.status === "canceled"
  );

  return (
    <div className="space-y-8 animate-fade-in">
      <DashboardHeader 
        title="Meu Painel" 
        description="Gerencie seus agendamentos e acompanhe seu progresso" 
      />

      {/* Se não há consultas, mostrar estado vazio com call-to-action */}
      {mappedAppointments.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle>Bem-vindo ao PsyOnline!</CardTitle>
              <CardDescription>
                Comece sua jornada de bem-estar agendando sua primeira consulta
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center py-8">
                <Calendar className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nenhuma consulta agendada</h3>
                <p className="text-muted-foreground mb-6">
                  Você ainda não possui consultas marcadas. Que tal agendar sua primeira sessão?
                </p>
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                  <Link to="/psychologists">
                    <Calendar className="h-4 w-4 mr-2" />
                    Agendar Primeira Consulta
                  </Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t">
                <div className="text-center">
                  <Clock className="h-8 w-8 mx-auto text-primary mb-2" />
                  <h4 className="font-medium">Flexibilidade</h4>
                  <p className="text-sm text-muted-foreground">Horários que se adaptam à sua rotina</p>
                </div>
                <div className="text-center">
                  <Users className="h-8 w-8 mx-auto text-primary mb-2" />
                  <h4 className="font-medium">Profissionais Qualificados</h4>
                  <p className="text-sm text-muted-foreground">Psicólogos experientes e verificados</p>
                </div>
                <div className="text-center">
                  <Calendar className="h-8 w-8 mx-auto text-primary mb-2" />
                  <h4 className="font-medium">Online</h4>
                  <p className="text-sm text-muted-foreground">Consultas no conforto da sua casa</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <ProfileCard user={user} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle>Minhas Consultas</CardTitle>
              <CardDescription>
                Veja suas próximas consultas e histórico
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AppointmentList 
                upcomingAppointments={upcomingAppointments} 
                pastAppointments={pastAppointments} 
              />
            </CardContent>
          </Card>

          <ProfileCard user={user} />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
